import { useEffect, useRef, useState } from 'react';
import { createConnection, startConnection, registerOnMessageReceived, closeConnection, unregisterOnMessageReceived } from '../services/signalRService';
import { WaterdataGroup } from '../types/userGroupPermissions';
import { GLOBAL_HD_DTU_ID } from './common';
import Api from '../api/Api';
import useAuthStore from "../store/authStore";

/**
 *
 * @param value Returns the previous value and store the current for next time.
 */
export function usePrevious<T>(value: T): T {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref: any = useRef<T>();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

export const useIsMounted = () => {
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return mounted;
};

export const useSubscriptionStatus = () => {
  const user = useAuthStore(state => state.user)

  const isActiveSubscriber = () => {
    if (!user) return false;
    if (user.waterdataGroup === WaterdataGroup.MOOD_Admin) return true
    if (!user.subscriptionExpiryDate) return false

    const subscriptionExpiryDate = new Date(user.subscriptionExpiryDate);
    const currentDate = new Date();

    return subscriptionExpiryDate > currentDate;
  };

  return {
    isActiveSubscriber: isActiveSubscriber(),
  };
};

export const useIsTnw = (datasetId: string) => {
  const [isTnw, setIsTnw] = useState<boolean>(false);

  useEffect(() => {
    setIsTnw(datasetId.toLowerCase().includes('tnw'));
  },  [datasetId]);

  return isTnw;
};

export const useIsDwf = (datasetId: string) => {
  const [isDwf, setIsDwf] = useState<boolean>(false)

  useEffect(() => {
    setIsDwf(datasetId.toLowerCase().includes('dwf'));
  },  [datasetId]);

  return isDwf;
}

export const useIsGlobalHDDtu = (datasetId: string) => {
  const [isHDDtu, setIsHDDtu] = useState<boolean>(false)

  useEffect(() => {
    setIsHDDtu(datasetId === GLOBAL_HD_DTU_ID);
  },  [datasetId]);

  return isHDDtu;
}

export const useSignalR = (messageType: string, messageString: string) => {
  const [messageReceived, setMessageReceived] = useState<boolean>(false);
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
  const [receivedMessageText, setReceivedMessageText] = useState<string>("");

  const user = useAuthStore(state => state.user)

  const setupConn = async () => {
    if (connection) {
      unregisterOnMessageReceived(connection, messageType);
    }

    const conn = createConnection(Api.SignalRServer);
    await startConnection(conn);
    setConnection(conn);
  }

  useEffect(() => {
    if(!connection || connection.state !== "Connected") {
      setupConn();
    }

    return () => {
      disconnect()
    }
  },[connection?.state]);

  useEffect(() => {
    if (connection) {
      unregisterOnMessageReceived(connection, messageType);

      registerOnMessageReceived(connection, messageType, (signalRUser, message) => {
        if (message.includes(messageString) && user.userName === signalRUser){
          setReceivedMessageText(message);
          setMessageReceived(true);
        }
      });
    }
  }, [connection, user])

  const resetMessageState = () => {
    setReceivedMessageText("");
    setMessageReceived(false);
  }

  const disconnect = async () => {
    if (connection) {
      closeConnection(connection);
    }
  };

  return { messageReceived, receivedMessageText, resetMessageState };
};

